import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import api from '../../api/client';
import StarRating from '../../components/StarRating';

const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#6b7280'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t] = await Promise.all([api.get('/tickets/stats'), api.get('/tickets?page=1&limit=5')]);
        if (!cancelled) {
          setStats(s.data);
          setRecent(t.data.tickets || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="tr-spinner" />
      </div>
    );
  }

  const statusPie = stats
    ? [
        { name: 'Open', value: stats.open },
        { name: 'In progress', value: stats.in_progress },
        { name: 'Resolved', value: stats.resolved },
        { name: 'Closed', value: stats.closed },
      ].filter((x) => x.value > 0)
    : [];

  const trendData = (stats?.trend || []).map((row) => ({
    date: row.date?.slice(5) || row.date,
    count: row.count,
  }));

  const topLoc = stats?.top_locations || [];

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Dashboard</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        {[
          { label: 'Total Tickets', key: 'total', sub: stats?.today },
          { label: 'Open', key: 'open', sub: stats?.today },
          { label: 'In Progress', key: 'in_progress', sub: stats?.today },
          { label: 'Resolved', key: 'resolved', sub: stats?.today },
        ].map((card) => (
          <div key={card.key} className="tr-card" style={{ padding: '1.25rem' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{card.label}</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary)' }}>{stats?.[card.key] ?? 0}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', minHeight: '1.1rem' }}>
              {card.key === 'total' ? `Today: ${stats?.today ?? 0} new` : '\u00a0'}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.25rem',
          marginBottom: '1.5rem',
        }}
      >
        <div className="tr-card" style={{ padding: '1rem', minHeight: 320 }}>
          <h3 style={{ marginTop: 0 }}>Ticket trend (7 days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#FF6B00" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="tr-card" style={{ padding: '1rem', minHeight: 320 }}>
          <h3 style={{ marginTop: 0 }}>Status distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} label>
                {statusPie.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.25rem',
          marginBottom: '1.5rem',
        }}
      >
        <div className="tr-card" style={{ padding: '1.25rem' }}>
          <h3 style={{ marginTop: 0 }}>Average rating</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--primary)' }}>{stats?.avg_rating?.toFixed(2) ?? '0.00'}</span>
            <StarRating value={Math.round(stats?.avg_rating || 0)} readOnly size={28} />
          </div>
        </div>

        <div className="tr-card" style={{ padding: '1.25rem' }}>
          <h3 style={{ marginTop: 0 }}>Top locations</h3>
          {topLoc.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No data yet.</p>}
          {topLoc.map((loc) => {
            const max = Math.max(...topLoc.map((l) => l.count), 1);
            const pct = (loc.count / max) * 100;
            return (
              <div key={loc.id} style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span>{loc.name}</span>
                  <span style={{ fontWeight: 600 }}>{loc.count}</span>
                </div>
                <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="tr-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0 }}>Recent tickets</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: 'var(--bg-muted)' }}>
                <th style={{ padding: '0.65rem 1rem' }}>Ticket</th>
                <th style={{ padding: '0.65rem 1rem' }}>Subject</th>
                <th style={{ padding: '0.65rem 1rem' }}>Status</th>
                <th style={{ padding: '0.65rem 1rem' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/admin/tickets/${row.id}`)}
                  style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  <td style={{ padding: '0.65rem 1rem', fontWeight: 600 }}>{row.ticket_number}</td>
                  <td style={{ padding: '0.65rem 1rem' }}>{row.subject}</td>
                  <td style={{ padding: '0.65rem 1rem' }}>
                    <span className={`badge badge-${row.status}`}>{row.status.replace('_', ' ')}</span>
                  </td>
                  <td style={{ padding: '0.65rem 1rem', color: 'var(--text-muted)' }}>{new Date(row.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
